/**
 * Core HTTP plumbing for every API call: `request<T>()` / `requestBlob()`, the
 * bearer-token glue, the `streamSSE()` parser, and the `ApiError` /
 * `StaleStateError` (412) failures. Per-feature endpoint modules import from
 * here and never roll their own fetch logic.
 */

import { clearSession, getToken, redirectToLogin } from '../auth'

export interface RequestOptions {
  ifMatch?: number | undefined
  /** WAFR item endpoints' item-scoped concurrency token (the `version` of the
   *  item the client saw) — sent as `X-Item-Version` instead of If-Match. */
  itemVersion?: number | undefined
}

/** A non-OK HTTP response. Branch on `status` (see `isApiStatus`), never on
 * the message text — a body can contain any digits. The message reads
 * `"<status> <statusText>: <body>"` (and `name` stays "Error") because that is
 * the text pages display. */
export class ApiError extends Error {
  status: number
  statusText: string
  body: string

  constructor(status: number, statusText: string, body: string, message?: string) {
    super(message ?? `${[status, statusText].filter(Boolean).join(' ')}: ${body}`)
    this.status = status
    this.statusText = statusText
    this.body = body
  }
}

/** True iff `e` is an ApiError for HTTP `status`. */
export function isApiStatus(e: unknown, status: number): boolean {
  return e instanceof ApiError && e.status === status
}

/** The only part of a 412's `current_state` the client reads. That snapshot is
 * whichever entity lost the race (assessment, company, module, WAFR review),
 * so these two fields are all the shapes have in common. */
export interface StaleSnapshot {
  revision: number
  updated_at: string
}

function staleSnapshot(raw: unknown): StaleSnapshot | null {
  if (typeof raw !== 'object' || raw === null) return null
  const { revision, updated_at } = raw as Record<string, unknown>
  if (typeof revision !== 'number') return null
  return { revision, updated_at: typeof updated_at === 'string' ? updated_at : '' }
}

/** Thrown when a mutating request loses a concurrency check (HTTP 412).
 * Carries the server's latest revision so the UI can rebase. */
export class StaleStateError extends ApiError {
  currentRevision: number
  currentState: StaleSnapshot | null
  serverMessage: string

  constructor(currentRevision: number, currentState: StaleSnapshot | null, serverMessage: string) {
    super(412, 'Precondition Failed', '', serverMessage)
    this.name = 'StaleStateError'
    this.currentRevision = currentRevision
    this.currentState = currentState
    this.serverMessage = serverMessage
  }
}

/** Throw the ApiError for a non-OK response (its body read as text). */
async function throwApiError(resp: Response, statusText = resp.statusText): Promise<never> {
  throw new ApiError(resp.status, statusText, await resp.text())
}

/** Inject the bearer token (if any) into the outgoing request headers. */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...(extra ?? {}) }
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

/** Single 401 handler: wipe the bad session and bounce to /login. */
export function handleUnauthorized(): void {
  clearSession()
  redirectToLogin()
}

function rejectUnauthorized(): never {
  handleUnauthorized()
  throw new Error('Unauthorized — redirecting to sign-in.')
}

/** POST to an SSE endpoint and invoke onEvent for each parsed `data: <json>` frame. */
export async function streamSSE(
  path: string,
  onEvent: (evt: Record<string, unknown>) => void,
): Promise<void> {
  const resp = await fetch(path, { method: 'POST', headers: authHeaders() })
  if (resp.status === 401) rejectUnauthorized()
  // No statusText: this path's message reads "<status>: <body>".
  if (!resp.ok || !resp.body) return throwApiError(resp, '')
  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let idx: number
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      emitFrame(buffer.slice(0, idx), onEvent)
      buffer = buffer.slice(idx + 2)
    }
  }
  // A stream can end without the final blank line; that last frame (often the
  // terminal `done`/`error` event) is still a frame.
  emitFrame(buffer + decoder.decode(), onEvent)
}

function emitFrame(raw: string, onEvent: (evt: Record<string, unknown>) => void): void {
  const chunk = raw.trim()
  if (!chunk.startsWith('data: ')) return
  try {
    onEvent(JSON.parse(chunk.slice(6)))
  } catch {
    // ignore — malformed event
  }
}

/** The `require_staff` refusal (`{detail:{error:"staff_only"}}`). Reads a
 *  clone, so a non-matching 403 still has its body for `throwApiError`. */
async function isStaffOnly(resp: Response): Promise<boolean> {
  const body = await resp.clone().json().catch(() => null)
  return body?.detail?.error === 'staff_only'
}

/** JSON-in, JSON-out fetch wrapper. Adds the bearer token, sets If-Match for
 * optimistic-locked endpoints, and translates 412 into StaleStateError, 401
 * into the redirect-to-login handler, and any other non-OK into ApiError. */
export async function request<T>(
  path: string,
  init?: RequestInit,
  opts?: RequestOptions,
): Promise<T> {
  // FormData bodies must NOT carry a JSON Content-Type — the browser has to
  // set `multipart/form-data; boundary=…` itself. Everything else is JSON.
  const isFormData = init?.body instanceof FormData
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...authHeaders(init?.headers as Record<string, string> | undefined),
  }
  if (opts?.ifMatch !== undefined) {
    headers['If-Match'] = String(opts.ifMatch)
  }
  if (opts?.itemVersion !== undefined) {
    headers['X-Item-Version'] = String(opts.itemVersion)
  }
  const resp = await fetch(path, { ...init, headers })
  if (resp.status === 401) rejectUnauthorized()
  if (resp.status === 403 && (await isStaffOnly(resp))) {
    // A customer session reached a staff-only endpoint — send it home.
    window.location.href = '/portal'
    throw new ApiError(403, 'Forbidden', '', 'This area is for AllCode staff.')
  }
  if (resp.status === 412) {
    const body = await resp.json().catch(() => null)
    const detail = body?.detail ?? body
    throw new StaleStateError(
      detail?.current_revision ?? 0,
      staleSnapshot(detail?.current_state),
      detail?.message ?? 'Stale state — reload required.',
    )
  }
  if (!resp.ok) await throwApiError(resp)
  return resp.json() as Promise<T>
}

/** Trigger a browser "Save as" for a Blob via a transient object URL + <a download>. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Binary-download sibling of request(): same auth + error path, returns the
 * Blob. No If-Match/412 semantics — downloads aren't optimistic-locked. */
export async function requestBlob(path: string, init?: RequestInit): Promise<Blob> {
  const resp = await fetch(path, {
    ...init,
    headers: authHeaders(init?.headers as Record<string, string> | undefined),
  })
  if (resp.status === 401) rejectUnauthorized()
  if (!resp.ok) await throwApiError(resp)
  return resp.blob()
}
